/**
 * Turn a saved deal into printable paperwork.
 *
 * Reads the deal, buyer, vehicle and dealer settings, shapes them into the
 * context the mappings expect, fills the forms and merges them into one PDF —
 * one print job, the way the paperwork already works.
 */

import { supabase } from "../../lib/supabase";
import { buildRetailPacket, FormFillError, fetchFromPublic } from "../../forms/fillPdf";
import type { FormContext } from "../../forms/mappings";
import { guideForWarranty } from "../../lib/warranty";
import { buildPaymentReceipt, type ReceiptPayment } from "../../forms/paymentReceipt";
import { appendWarrantyPage } from "../../forms/warrantyPage";
import { PDFDocument } from "pdf-lib";

/** The Buyers Guide depends on whether a warranty was sold, so the packet is
    built per deal rather than being a fixed list. */
export const packetFor = (warrantyDays: number) => [
  "purchase_agreement", "hsmv_82040", "hsmv_82993", "hsmv_83330",
  guideForWarranty(warrantyDays),
];

export const AVAILABLE_PACKET = packetFor(0);

/** Printed on its own, before there is a contract. */
export const DEPOSIT_PACKET = ["deposit_receipt"];

export const PACKET_LABELS: Record<string, string> = {
  purchase_agreement: "Purchase agreement",
  deposit_receipt: "Deposit receipt",
  hsmv_82040: "Title application (82040)",
  hsmv_82993: "Odometer disclosure (82993)",
  hsmv_83330: "Insurance affidavit (83330)",
  buyers_guide_en: "Buyers Guide (as-is)",
  buyers_guide_warranty: "Buyers Guide (with warranty)",
};

const DEAL_SELECT = `*,
  buyer:customers!deals_buyer_id_fkey(*),
  co_buyer:customers!deals_co_buyer_id_fkey(*),
  vehicle:vehicles!deals_vehicle_id_fkey(*)`;

type Row = Record<string, any>;

function person(p: Row) {
  return {
    firstName: String(p.first_name ?? ""),
    middleName: (p.middle_name as string) ?? null,
    lastName: String(p.last_name ?? ""),
    suffix: (p.suffix as string) ?? null,
    dateOfBirth: String(p.date_of_birth ?? ""),
    idNumber: String(p.id_number ?? ""),
    idState: String(p.id_state ?? ""),
    /* A business buyer: the company is the applicant on the 82040 and this
       person is the officer who signs for it. */
    isBusiness: p.is_business === true,
    businessName: (p.business_name as string) ?? null,
    businessEin: (p.business_ein as string) ?? null,
    businessTitle: (p.business_title as string) ?? null,
    street: String(p.street ?? ""),
    city: String(p.city ?? ""),
    county: (p.county as string) ?? null,
    state: String(p.state ?? ""),
    zip: String(p.zip ?? ""),
    mailSameAsPhysical: p.mail_same_as_physical !== false,
    mailStreet: (p.mail_street as string) ?? null,
    mailCity: (p.mail_city as string) ?? null,
    mailState: (p.mail_state as string) ?? null,
    mailZip: (p.mail_zip as string) ?? null,
    phoneCell: (p.phone_cell as string) ?? null,
    email: (p.email as string) ?? null,
  };
}

/**
 * Shape a deal row plus dealer settings into the context every document
 * needs. Shared, so the packet and the receipt cannot describe the same deal
 * differently.
 */
function contextFor(deal: Row, dealer: Row): FormContext {
  const b = deal.buyer;
  const v = deal.vehicle;
  if (!b || !v) throw new Error("This deal is missing its buyer or vehicle.");

  return {
    dealer: {
      legalName: dealer.legal_name,
      dba: dealer.dba,
      flhsmvLicenseNo: dealer.flhsmv_license_no,
      salesTaxRegNo: dealer.sales_tax_reg_no,
      street: dealer.street, city: dealer.city,
      state: dealer.state, zip: dealer.zip, county: dealer.county,
      phone: dealer.phone, email: dealer.email,
    },
    buyer: person(b),
    /* The second buyer, when there is one. The forms have carried co-owner
       fields all along; nothing ever handed them a person. */
    coBuyer: deal.co_buyer ? person(deal.co_buyer) : null,
    vehicle: {
      vin: v.vin, year: v.year, make: v.make, model: v.model,
      askingPriceCents: v.asking_price_cents,
      bodyType: v.body_type, exteriorColor: v.exterior_color,
      odometer: v.odometer, weight: null,
      titleNumber: v.title_number, titleState: v.title_state,
      titleBrand: v.title_brand,
    },
    trade: null,
    deal: {
      dealNo: deal.deal_no,
      agreementDate: deal.agreement_date,
      cashPriceCents: deal.cash_price_cents,
      stateTaxCents: deal.state_tax_cents,
      surtaxCents: deal.surtax_cents,
      jointOwnership: (deal.joint_ownership as "and" | "or" | null) ?? null,
      tagChoice: (deal.tag_choice as "transfer" | "new") ?? "new",
      tagNumber: (deal.tag_number as string) ?? null,
    },
    odometerReadDate: deal.agreement_date,
    odometerStatus: 1,
    stockNo: v.stock_no ?? "",
    warrantyDays: deal.warranty_days ?? 0,
    warrantyMiles: deal.warranty_miles ?? 0,
    insurance: {
      company: deal.insurance_company ?? null,
      policyNo: deal.insurance_policy_no ?? null,
      companyCode: deal.insurance_company_code ?? null,
    },
    // Amounts come straight from the database's generated columns, so the
    // printed contract cannot disagree with the deal screen.
    amounts: {
      cashPrice: deal.cash_price_cents,
      predelivery: deal.predelivery_service_cents,
      efiling: deal.electronic_filing_cents,
      tradeAllowance: deal.trade_allowance_cents,
      taxable: deal.taxable_amount_cents,
      stateTax: deal.state_tax_cents,
      surtax: deal.surtax_cents,
      totalSales: deal.total_sales_price_cents,
      licenseReg: deal.license_registration_cents,
      titleFee: deal.title_fee_cents,
      tempTag: deal.temp_tag_cents,
      tradePayoff: deal.trade_payoff_cents,
      totalDue: deal.total_amount_due_cents,
      cashDown: deal.cash_down_cents,
      deferredDown: deal.deferred_down_cents,
      unpaid: deal.unpaid_balance_cents,
    },
  };
}

async function loadDeal(dealId: string) {
  const [{ data: deal, error: dErr }, { data: dealer, error: sErr }] =
    await Promise.all([
      supabase.from("deals").select(DEAL_SELECT).eq("id", dealId).single(),
      supabase.from("dealer_settings").select("*").maybeSingle(),
    ]);

  if (dErr || !deal) throw new Error("Could not load the deal.");
  if (sErr) throw new Error("Could not load dealer settings.");
  if (!dealer) {
    throw new Error("Fill in Settings first — the forms need your dealer details.");
  }
  return { deal: deal as Row, dealer: dealer as Row };
}

/**
 * The same context the packet is built from, for documents that live outside
 * the packet — the We Owe, for one. Exported so there is exactly one place
 * that decides what a deal's buyer, vehicle and dealer look like on paper.
 */
export async function loadDealContext(dealId: string): Promise<FormContext> {
  const { deal, dealer } = await loadDeal(dealId);
  return contextFor(deal, dealer);
}

export async function buildPacketForDeal(
  dealId: string,
  forms?: string[],
): Promise<Uint8Array> {
  const { deal, dealer } = await loadDeal(dealId);
  const ctx = contextFor(deal, dealer);
  const chosen = forms ?? packetFor(deal.warranty_days ?? 0);

  // The deposit receipt prefills from the first money taken, if any.
  if (chosen.includes("deposit_receipt")) {
    const { data: first } = await supabase
      .from("payments").select("*").eq("deal_id", dealId)
      // A reversed payment is not money the dealer holds, so it cannot stand
      // in for the deposit on a receipt.
      .is("reversed_at", null)
      .order("paid_on").limit(1).maybeSingle();
    if (first) {
      ctx.deposit = {
        amountCents: first.amount_cents,
        ...describePayment(first),
        takenOn: first.paid_on,
      };
    }
  }

  try {
    const packet = await buildRetailPacket(ctx, fetchFromPublic, chosen);
    return withWarrantyTerms(packet.bytes, ctx);
  } catch (e) {
    if (e instanceof FormFillError) {
      if (e.report) console.error("Form fill report:", e.report);
      throw new Error(e.message);
    }
    throw e;
  }
}

/**
 * Put the warranty terms on the end of a packet.
 *
 * The terms are pages drawn by hand, not a field that gets filled: the Buyers
 * Guide's SYSTEMS COVERED box only holds a one-line summary that ends "Full
 * terms attached", and the attaching happens here.
 *
 * This used to live inside buildPacketForDeal, which meant only the Print
 * button did it. A packet built for signing went out with a Buyers Guide
 * promising terms that were not in the document — and the Buyers Guide is
 * part of the contract. Every path that produces a packet goes through here
 * now, which is the only way the two can stay in step.
 *
 * Returns the bytes untouched when the car is sold as-is: there are no terms.
 */
export async function withWarrantyTerms(
  bytes: Uint8Array, ctx: FormContext,
): Promise<Uint8Array> {
  const days = ctx.warrantyDays ?? 0;
  if (!days) return bytes;

  const doc = await PDFDocument.load(bytes);
  await appendWarrantyPage(
    doc, days,
    ctx.dealer.dba ?? ctx.dealer.legalName,
    { year: ctx.vehicle.year, make: ctx.vehicle.make,
      model: ctx.vehicle.model, vin: ctx.vehicle.vin },
    ctx.deal.dealNo,
  );
  return doc.save();
}

/**
 * Money receipt for a deal. Says "paid in full" when nothing is outstanding,
 * which is what a customer actually wants to walk out with.
 */
/**
 * How a payment reads on a printed receipt.
 *
 * A customer holding this later wants to recognise the payment on their card
 * statement, so the card and its last four go on the line. The Stripe
 * reference goes on too — it is what either side quotes if the charge is ever
 * queried. Never a card number: only the last four are stored anywhere.
 */
function describePayment(p: { method: string; reference_no: string | null;
                              card_brand?: string | null; card_last4?: string | null }) {
  if (p.card_last4) {
    const brand = p.card_brand
      ? p.card_brand.charAt(0).toUpperCase() + p.card_brand.slice(1)
      : "Card";
    return {
      method: `${brand} ending ${p.card_last4}`,
      reference: p.reference_no,
    };
  }
  return { method: p.method, reference: p.reference_no };
}

export async function buildReceiptForDeal(dealId: string): Promise<Uint8Array> {
  const { deal, dealer } = await loadDeal(dealId);
  const ctx = contextFor(deal, dealer);

  const { data: pays } = await supabase
    // Reversed payments are left off: a receipt lists money received, and a
    // bounced check is not that. The balance follows from the same rule.
    .from("payments").select("*").eq("deal_id", dealId)
    .is("reversed_at", null).order("paid_on");

  const payments: ReceiptPayment[] = (pays ?? []).map((p) => ({
    paidOn: p.paid_on,
    amountCents: p.amount_cents,
    ...describePayment(p),
  }));

  return buildPaymentReceipt(ctx, payments, deal.total_amount_due_cents);
}

/** Open the PDF in a new tab so the browser's own print dialog runs. */
export function openForPrint(bytes: Uint8Array, filename: string) {
  // Copy into a plain ArrayBuffer. A Uint8Array may be a view onto a shared
  // buffer, which BlobPart does not accept, and the copy is a few hundred KB.
  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const blob = new Blob([buffer], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    // Popup blocked — fall back to a download so the work is not lost.
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
  }
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
